import {useState} from 'react'
import {Settings, Edit, Code2, CheckCircle2, AlertCircle, XCircle} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Switch} from '@/components/ui/switch'
import {Label} from '@/components/ui/label'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Stack} from '@/components/shared/Stack'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormSection} from '@/components/shared/FormSection'
import {FormField} from '@/components/shared/FormField'
import {Input} from '@/components/ui/input'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {ErrorBanner} from '@/components/ui/error-banner'
import {InlineRow} from '@/components/shared/InlineRow'
import {useI18n} from '@/i18n'
import {AuthType} from '@/types/outbound-connector'
import type {
    OutboundConnector,
    ScriptOutboundConfigCreateRequest,
    ScriptOutboundConfigUpdateRequest,
    ScriptValidationResult,
} from '@/types/outbound-connector'
import {
    ConnectorConfigForm,
    type ConnectorAuthFormState,
} from '@/features/outbound-connectors/components/forms/ConnectorConfigForm'
import {ScriptEditorCore} from '@/components/shared/ScriptEditorCore'
import {
    useCreateScriptOutboundConfig,
    useUpdateScriptOutboundConfig,
    useValidateOutboundScript,
} from '@/hooks/use-outbound-connectors'
import {useDialog} from '@/hooks/useDialog'

// ─── Operations list (571.06: 4 boolean gates) ────────────────────────────────

type OutboundOpKey = 'create' | 'update' | 'delete' | 'query'

const OUTBOUND_OPERATIONS: ReadonlyArray<{key: OutboundOpKey; labelKey: string}> = [
    {key: 'create', labelKey: 'createOp'},
    {key: 'update', labelKey: 'updateOp'},
    {key: 'delete', labelKey: 'deleteOp'},
    {key: 'query', labelKey: 'queryOp'},
] as const

function buildDefaultEnabledOperations(): Record<OutboundOpKey, boolean> {
    return {create: true, update: true, delete: true, query: true}
}

// ─── Default template ─────────────────────────────────────────────────────────

const DEFAULT_OUTBOUND_SCRIPT = `/*
 * Outbound connector script — one blob with all operation methods.
 *
 * Shared helpers visible to all methods:
 */

def buildUserPayload() {
    return [
        userName : ctx.attributes['email'],
        firstName: ctx.attributes['firstName'],
        lastName : ctx.attributes['lastName'],
        email    : ctx.attributes['email']
    ]
}

def errorResult(response) {
    return [success: false, statusCode: response.statusCode, error: response.body?.message ?: "HTTP \${response.statusCode}", retryable: response.statusCode >= 500]
}

// ─── testConnection ───────────────────────────────────────────────────────────

def testConnection() {
    def start = System.currentTimeMillis()
    def response = connector.http.get("/api/health").execute()
    def duration = System.currentTimeMillis() - start
    if (response.statusCode in [200, 204]) { return [success: true, message: "Connected (\${duration}ms)"] }
    return [success: false, message: "HTTP \${response.statusCode} (\${duration}ms)"]
}

// ─── grant ────────────────────────────────────────────────────────────────────

def grant() {
    log.info("Granting {} to account {}", ctx.enforcementObjectExternalId, ctx.accountExternalId)
    def response = connector.http.post("/api/users/\${ctx.accountExternalId}/roles")
        .body([roleId: ctx.enforcementObjectExternalId]).asJson()
    if (response.statusCode in [200, 201]) { return [success: true] }
    if (response.statusCode == 409) { return [success: true] }
    return errorResult(response)
}

// ─── revoke ───────────────────────────────────────────────────────────────────

def revoke() {
    log.info("Revoking {} from account {}", ctx.enforcementObjectExternalId, ctx.accountExternalId)
    def response = connector.http.delete("/api/users/\${ctx.accountExternalId}/roles/\${ctx.enforcementObjectExternalId}").execute()
    if (response.statusCode in [200, 204, 404]) { return [success: true] }
    return [success: false, error: "HTTP \${response.statusCode}", retryable: response.statusCode >= 500]
}

// ─── createAccount ────────────────────────────────────────────────────────────

def createAccount() {
    def payload = buildUserPayload()
    log.info("Creating account for {}", payload.userName)
    def response = connector.http.post("/api/users").body(payload).asJson()
    if (response.statusCode in [200, 201]) { return [success: true, externalId: response.body.id.toString()] }
    return errorResult(response)
}

// ─── updateAccount ────────────────────────────────────────────────────────────

def updateAccount() {
    def payload = buildUserPayload()
    log.info("Updating account {}", ctx.accountExternalId)
    def response = connector.http.put("/api/users/\${ctx.accountExternalId}").body(payload).asJson()
    if (response.statusCode in [200, 204]) { return [success: true] }
    return errorResult(response)
}

// ─── enableAccount ────────────────────────────────────────────────────────────

def enableAccount() {
    log.info("Enabling account {}", ctx.accountExternalId)
    def response = connector.http.put("/api/users/\${ctx.accountExternalId}").body([active: true]).asJson()
    if (response.statusCode in [200, 204]) { return [success: true] }
    return [success: false, error: "HTTP \${response.statusCode}", retryable: response.statusCode >= 500]
}

// ─── disableAccount ───────────────────────────────────────────────────────────

def disableAccount() {
    log.info("Disabling account {}", ctx.accountExternalId)
    def response = connector.http.put("/api/users/\${ctx.accountExternalId}").body([active: false]).asJson()
    if (response.statusCode in [200, 204]) { return [success: true] }
    return [success: false, error: "HTTP \${response.statusCode}", retryable: response.statusCode >= 500]
}

// ─── deleteAccount ────────────────────────────────────────────────────────────

def deleteAccount() {
    log.info("Deleting account {}", ctx.accountExternalId)
    def response = connector.http.delete("/api/users/\${ctx.accountExternalId}").execute()
    if (response.statusCode in [200, 204, 404]) { return [success: true] }
    return [success: false, error: "HTTP \${response.statusCode}", retryable: response.statusCode >= 500]
}
`

// ─── Types ───────────────────────────────────────────────────────────────────

interface ScriptFormState extends ConnectorAuthFormState {
    baseUrl: string
    script: string
    enabledOperations: Record<OutboundOpKey, boolean>
    connectTimeoutMs: number
    readTimeoutMs: number
    version: number
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_AUTH_STATE: ConnectorAuthFormState = {
    authType: AuthType.NONE,
    tokenSecretRef: '',
    tokenUrl: '',
    clientId: '',
    clientSecretRef: '',
    scope: '',
    username: '',
    passwordSecretRef: '',
}

const DEFAULT_FORM: ScriptFormState = {
    ...DEFAULT_AUTH_STATE,
    baseUrl: '',
    script: DEFAULT_OUTBOUND_SCRIPT,
    enabledOperations: buildDefaultEnabledOperations(),
    connectTimeoutMs: 5000,
    readTimeoutMs: 10000,
    version: 0,
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface ScriptConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

// ─── Operation badge helpers ──────────────────────────────────────────────────

type BadgeKind = 'supported' | 'unsupported' | 'disabled'

interface OpBadgeState {
    key: OutboundOpKey
    labelKey: string
    kind: BadgeKind
}

function computeOpBadges(
    enabledOperations: Record<OutboundOpKey, boolean>,
    validationResult: ScriptValidationResult | null,
): OpBadgeState[] {
    return OUTBOUND_OPERATIONS.map(({key, labelKey}) => {
        const isEnabled = enabledOperations[key] !== false
        if (!isEnabled) {
            return {key, labelKey, kind: 'disabled'}
        }
        if (validationResult) {
            const supported = validationResult.supportedOperations ?? []
            const unsupported = validationResult.unsupportedOperations ?? []
            if (supported.includes(key)) {
                return {key, labelKey, kind: 'supported'}
            }
            if (unsupported.includes(key)) {
                return {key, labelKey, kind: 'unsupported'}
            }
        }
        return {key, labelKey, kind: 'supported'}
    })
}

function opBadgeVariant(kind: BadgeKind): 'success' | 'warning' | 'secondary' {
    if (kind === 'supported') return 'success'
    if (kind === 'unsupported') return 'warning'
    return 'secondary'
}

function OpBadgeIcon({kind}: {kind: BadgeKind}) {
    if (kind === 'supported') return <CheckCircle2 className="h-3 w-3 mr-1"/>
    if (kind === 'unsupported') return <AlertCircle className="h-3 w-3 mr-1"/>
    return <XCircle className="h-3 w-3 mr-1"/>
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ScriptConfigCard({connector, canUpdate}: ScriptConfigCardProps) {
    const i18n = useI18n()
    const scriptConfig = connector.scriptConfig

    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<ScriptFormState>(DEFAULT_FORM)
    const [validationResult, setValidationResult] = useState<ScriptValidationResult | null>(null)
    const [validationNetworkError, setValidationNetworkError] = useState<Error | null>(null)

    const createMutation = useCreateScriptOutboundConfig(connector.id)
    const updateMutation = useUpdateScriptOutboundConfig(connector.id)
    const validateMutation = useValidateOutboundScript(connector.id)

    const updateForm = (updates: Partial<ScriptFormState>) =>
        setForm((prev) => ({...prev, ...updates}))

    const openCreate = () => {
        setForm({...DEFAULT_FORM, enabledOperations: buildDefaultEnabledOperations()})
        setValidationResult(null)
        setValidationNetworkError(null)
        createDialog.open()
    }

    const openEdit = () => {
        if (!scriptConfig) return
        setForm({
            authType: scriptConfig.authType,
            tokenSecretRef: '',
            tokenUrl: scriptConfig.tokenUrl ?? '',
            clientId: scriptConfig.clientId ?? '',
            clientSecretRef: '',
            scope: scriptConfig.scope ?? '',
            username: scriptConfig.username ?? '',
            passwordSecretRef: '',
            baseUrl: scriptConfig.baseUrl ?? '',
            script: scriptConfig.script,
            enabledOperations: {
                create: scriptConfig.createEnabled,
                update: scriptConfig.updateEnabled,
                delete: scriptConfig.deleteEnabled,
                query: scriptConfig.queryEnabled,
            },
            connectTimeoutMs: scriptConfig.connectTimeoutMs,
            readTimeoutMs: scriptConfig.readTimeoutMs,
            version: scriptConfig.version,
        })
        setValidationResult(null)
        setValidationNetworkError(null)
        editDialog.open()
    }

    /**
     * Run the connector-scoped validate endpoint and update local state.
     * Returns the structured result so save handlers can gate on `.valid`.
     */
    const runValidation = async (): Promise<ScriptValidationResult | null> => {
        setValidationNetworkError(null)
        try {
            const result = (await validateMutation.mutateAsync({script: form.script})) as ScriptValidationResult
            setValidationResult(result)
            return result
        } catch (err) {
            setValidationNetworkError(err as Error)
            setValidationResult(null)
            return null
        }
    }

    const handleCreate = async () => {
        const validation = await runValidation()
        if (!validation || !validation.valid) return
        const request: ScriptOutboundConfigCreateRequest = {
            baseUrl: form.baseUrl || null,
            authType: form.authType,
            authSecretRef: form.tokenSecretRef || null,
            tokenUrl: form.tokenUrl || null,
            clientId: form.clientId || null,
            clientSecretRef: form.clientSecretRef || null,
            scope: form.scope || null,
            username: form.username || null,
            passwordSecretRef: form.passwordSecretRef || null,
            apiKeyHeader: null,
            apiKeySecretRef: null,
            script: form.script || null,
            createEnabled: form.enabledOperations.create,
            updateEnabled: form.enabledOperations.update,
            deleteEnabled: form.enabledOperations.delete,
            queryEnabled: form.enabledOperations.query,
            connectTimeoutMs: form.connectTimeoutMs,
            readTimeoutMs: form.readTimeoutMs,
        }
        createMutation.mutate(request, {onSuccess: () => createDialog.close()})
    }

    const handleUpdate = async () => {
        const validation = await runValidation()
        if (!validation || !validation.valid) return
        const request: ScriptOutboundConfigUpdateRequest = {
            version: form.version,
            baseUrl: form.baseUrl || null,
            authType: form.authType,
            authSecretRef: form.tokenSecretRef || null,
            tokenUrl: form.tokenUrl || null,
            clientId: form.clientId || null,
            clientSecretRef: form.clientSecretRef || null,
            scope: form.scope || null,
            username: form.username || null,
            passwordSecretRef: form.passwordSecretRef || null,
            apiKeyHeader: null,
            apiKeySecretRef: null,
            script: form.script || null,
            createEnabled: form.enabledOperations.create,
            updateEnabled: form.enabledOperations.update,
            deleteEnabled: form.enabledOperations.delete,
            queryEnabled: form.enabledOperations.query,
            connectTimeoutMs: form.connectTimeoutMs,
            readTimeoutMs: form.readTimeoutMs,
        }
        updateMutation.mutate(request, {onSuccess: () => editDialog.close()})
    }

    const handleValidate = () => {
        void runValidation()
    }

    const getAuthLabel = (type: AuthType) => {
        switch (type) {
            case AuthType.BEARER_TOKEN: return i18n.connectors.bearerToken
            case AuthType.OAUTH2_CLIENT: return i18n.connectors.oauth2Client
            case AuthType.BASIC_AUTH: return i18n.connectors.basicAuth
            case AuthType.NONE: return i18n.connectors.noneAuth
            case AuthType.TOKEN: return i18n.connectors.tokenAuth
            case AuthType.CERTIFICATE: return i18n.connectors.certificateAuth
            case AuthType.KUBECONFIG: return i18n.connectors.kubeconfigAuth
        }
    }

    const getOperationLabel = (labelKey: string): string => {
        const key = labelKey as keyof typeof i18n.scriptConnector.operationLabels
        return i18n.scriptConnector.operationLabels[key] as string
    }

    const opBadges = scriptConfig
        ? computeOpBadges(
            {
                create: scriptConfig.createEnabled,
                update: scriptConfig.updateEnabled,
                delete: scriptConfig.deleteEnabled,
                query: scriptConfig.queryEnabled,
            },
            null,
        )
        : []

    const formContent = (mode: 'create' | 'edit') => (
        <form
            onSubmit={(e) => {
                e.preventDefault()
                if (mode === 'create') { handleCreate() } else { handleUpdate() }
            }}
        >
            <div className="grid gap-6 py-4">
                {/* Auth + HTTP via ConnectorConfigForm */}
                <ConnectorConfigForm
                    form={{
                        ...form,
                        maxRetries: 0,
                        retryBackoffMs: 0,
                    }}
                    onChange={(updates) => updateForm(updates as Partial<ScriptFormState>)}
                    mode={mode}
                    showRetryFields={false}
                    authTypeOptions={[
                        AuthType.NONE,
                        AuthType.BEARER_TOKEN,
                        AuthType.BASIC_AUTH,
                        AuthType.OAUTH2_CLIENT,
                    ]}
                />

                {/* Base URL */}
                <FormSection title={i18n.scriptConnector.sectionBaseUrl}>
                    <FormGrid columns={1}>
                        <FormField label={i18n.scriptConnector.baseUrl} htmlFor="script-baseUrl">
                            <Input
                                id="script-baseUrl"
                                value={form.baseUrl}
                                onChange={(e) => updateForm({baseUrl: e.target.value})}
                                placeholder={i18n.scriptConnector.baseUrlPlaceholder}
                            />
                        </FormField>
                    </FormGrid>
                </FormSection>

                {/* Operations toggles */}
                <FormSection title={i18n.scriptConnector.sectionOperations}>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3"> {OUTBOUND_OPERATIONS.map(({key, labelKey}) => (
                            <div key={key} className="flex items-center gap-2 py-1"> <Switch
                                    id={`op-toggle-${key}`}
                                    data-testid={`switch-op-${key.toLowerCase()}`}
                                    checked={form.enabledOperations[key] !== false}
                                    onCheckedChange={(checked) =>
                                        updateForm({
                                            enabledOperations: {...form.enabledOperations, [key]: checked},
                                        })
                                    }
                                />
                                <Label htmlFor={`op-toggle-${key}`} className="text-sm">
                                    {getOperationLabel(labelKey)}
                                </Label>
                            </div>
                        ))}
                    </div>
                </FormSection>

                {/* Single script editor */}
                <Stack>
                    <div className="flex items-center justify-between">
                        <Strong>{i18n.scriptConnector.sectionScript}</Strong>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid="btn-validate-script"
                            onClick={handleValidate}
                            disabled={validateMutation.isPending}
                        >
                            {i18n.scriptConnector.validateScript}
                        </Button>
                    </div>
                    <MutedText size="sm">{i18n.scriptConnector.scriptDescription}</MutedText>

                    {/* Validation network error */}
                    {validationNetworkError && (
                        <ErrorBanner error={validationNetworkError}/>
                    )}

                    {/* Validation result */}
                    {validationResult && (
                        <ValidationResultBanner result={validationResult} i18n={i18n}/>
                    )}

                    {/* Operation badges (post-validate) */}
                    {validationResult && (
                        <div className="flex flex-wrap gap-2">
                            {OUTBOUND_OPERATIONS.map(({key, labelKey}) => {
                                const badges = computeOpBadges(form.enabledOperations, validationResult)
                                const badge = badges.find((b) => b.key === key)!
                                return (
                                    <Badge
                                        key={key}
                                        variant={opBadgeVariant(badge.kind)}
                                        data-testid={`badge-op-${key.toLowerCase()}`}
                                        className="flex items-center"
                                    >
                                        <OpBadgeIcon kind={badge.kind}/>
                                        {getOperationLabel(labelKey)}
                                    </Badge>
                                )
                            })}
                        </div>
                    )}

                    <div className="border rounded-md overflow-hidden min-h-[400px]"> <ScriptEditorCore
                            script={form.script}
                            onScriptChange={(s) => updateForm({script: s})}
                            hideSyntaxToolbar
                            externalSyntaxErrors={validationResult && !validationResult.valid ? validationResult.errors : null}
                        />
                    </div>
                </Stack>
            </div>

            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-script' : 'edit-script'}
                onCancel={() =>
                    mode === 'create' ? createDialog.close() : editDialog.close()
                }
                saving={
                    mode === 'create' ? createMutation.isPending : updateMutation.isPending
                }
                saveLabel={
                    mode === 'create'
                        ? i18n.connectors.saveConfiguration
                        : i18n.common.saveChanges
                }
            />
        </form>
    )

    return (
        <>
            <Card
                data-testid="script-config-card"
            >
                <RowCardHeader>
                    <div>
                        <CardTitle
                            className="items-center gap-2"
                            data-testid="card-title-script-config"
                        >
                            <Code2 className="h-5 w-5"/>
                            {i18n.scriptConnector.title}
                        </CardTitle>
                        <CardDescription>{i18n.scriptConnector.description}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                        {canUpdate && (
                            scriptConfig ? (
                                <Button
                                    data-testid="btn-edit-script-config"
                                    size="sm"
                                    onClick={openEdit}
                                >
                                    <Icon icon={Edit} className="mr-2"/>
                                    {i18n.common.edit}
                                </Button>
                            ) : (
                                <Button
                                    data-testid="btn-create-script-config"
                                    size="sm"
                                    onClick={openCreate}
                                >
                                    <Icon icon={Settings} className="mr-2"/>
                                    {i18n.scriptConnector.createConfig}
                                </Button>
                            )
                        )}
                    </div>
                </RowCardHeader>
                <CardContent>
                    {scriptConfig ? (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">
                                    {i18n.scriptConnector.baseUrl}
                                </dt>
                                <dd>
                                    {scriptConfig.baseUrl ? (
                                        <CodeText>{scriptConfig.baseUrl}</CodeText>
                                    ) : (
                                        <MutedText size="sm">{i18n.scriptConnector.noBaseUrl}</MutedText>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">
                                    {i18n.scriptConnector.authType}
                                </dt>
                                <dd>
                                    <CodeText>{getAuthLabel(scriptConfig.authType)}</CodeText>
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">
                                    {i18n.scriptConnector.operations}
                                </dt>
                                <dd className="mt-1 flex flex-wrap gap-1">
                                    {opBadges.map(({key, labelKey, kind}) => (
                                        <Badge
                                            key={key}
                                            variant={opBadgeVariant(kind)}
                                            data-testid={`badge-op-display-${key.toLowerCase()}`}
                                            className="flex items-center"
                                        >
                                            <OpBadgeIcon kind={kind}/>
                                            {getOperationLabel(labelKey)}
                                        </Badge>
                                    ))}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">
                                    {i18n.scriptConnector.script}
                                </dt>
                                <dd className="mt-1">
                                    {scriptConfig.script ? (
                                        <Badge variant="secondary">{i18n.scriptConnector.configured}</Badge>
                                    ) : (
                                        <MutedText size="sm">{i18n.scriptConnector.notConfigured}</MutedText>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">
                                    {i18n.scriptConnector.connectTimeoutMs}
                                </dt>
                                <dd className="mt-1">
                                    {i18n.common.valueWithMs({value: scriptConfig.connectTimeoutMs})}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">
                                    {i18n.scriptConnector.readTimeoutMs}
                                </dt>
                                <dd className="mt-1">
                                    {i18n.common.valueWithMs({value: scriptConfig.readTimeoutMs})}
                                </dd>
                            </div>
                        </dl>
                    ) : (
                        <MutedText block centered className="py-4">
                            {i18n.scriptConnector.notConfigured}
                        </MutedText>
                    )}
                </CardContent>
            </Card>

            {/* Create dialog */}
            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"> <DialogHeader>
                        <DialogTitle>{i18n.scriptConnector.createDialogTitle}</DialogTitle>
                        <DialogDescription>{i18n.scriptConnector.createDialogDescription}</DialogDescription>
                    </DialogHeader>
                    {formContent('create')}
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"> <DialogHeader>
                        <DialogTitle>{i18n.scriptConnector.editDialogTitle}</DialogTitle>
                        <DialogDescription>{i18n.scriptConnector.editDialogDescription}</DialogDescription>
                    </DialogHeader>
                    {formContent('edit')}
                </DialogContent>
            </Dialog>

        </>
    )
}

// ─── Validation banner ────────────────────────────────────────────────────────

function ValidationResultBanner({result, i18n}: {result: ScriptValidationResult; i18n: ReturnType<typeof useI18n>}) {
    if (result.valid) {
        return (
            <InlineRow gap="sm" className="p-3 rounded-md border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950"> <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0"/>
                <div>
                    <MutedText>{i18n.scriptConnector.validationSuccess}</MutedText>
                    {result.warnings.length > 0 && (
                        <ul className="mt-1 list-disc pl-4 text-sm text-amber-700 dark:text-amber-400">
                            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    )}
                </div>
            </InlineRow>
        )
    }
    const formattedErrors = result.errors
        .map((e) => (e.line > 0
            ? i18n.scriptConnector.errorAtLine({line: String(e.line), column: String(e.column), message: e.message})
            : e.message))
        .join('\n')
    return (
        <div className="p-3 rounded-md border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"> <InlineRow gap="sm">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0"/>
                <MutedText>{i18n.scriptConnector.validationError}</MutedText>
            </InlineRow>
            <ErrorBanner error={formattedErrors ? new Error(formattedErrors) : null}/>
        </div>
    )
}
